import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IconButton } from '@components/button/Button';
import Spinner from '@components/spinner/Spinner';
import {
  removeAllDownloads,
  removeDownload,
  updateDownloadStatus
} from '@features/downloads/downloadsSlice';

import Icon from '../icon/Icon';

import { DownloadItem, DownloadStatus } from '../../types/downloads';

const DownloadProgressPanel: React.FC = () => {
  const dispatch = useDispatch();
  const downloads = useSelector(
    (state: any) => state.downloads.items
  ) as DownloadItem[];

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    const completedDownloads = downloads.filter(
      (download) =>
        download.status === 'completed' || download.status === 'cancelled'
    );

    completedDownloads.forEach((download: DownloadItem) => {
      const timeSinceCompletion = Date.now() - download.timestamp;
      const timeUntilRemoval = 5000 - timeSinceCompletion;

      if (timeUntilRemoval <= 0) {
        dispatch(removeDownload(download.id));
      } else {
        const timer = setTimeout(() => {
          dispatch(removeDownload(download.id));
        }, timeUntilRemoval);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [downloads, dispatch]);

  const getDownloadStatusColor = (status: DownloadStatus) => {
    switch (status) {
      case 'completed':
        return 'text-green-500';
      case 'cancelled':
        return 'text-red-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-blue-500';
    }
  };

  const handleCancelDownload = (downloadId: string) => {
    dispatch(
      updateDownloadStatus({
        id: downloadId,
        status: 'cancelled'
      })
    );
  };

  const getDownloadStatusIcon = (
    status: DownloadStatus,
    downloadId: string
  ) => {
    switch (status) {
      case 'completed':
        return <Icon name="tick-with-circle" />;
      case 'cancelled':
        return null;
      case 'error':
        return <Icon name="error" />;
      default:
        return (
          <div className="group relative">
            <Spinner
              size="sm"
              className="transition-opacity group-hover:opacity-0"
            />
            <IconButton
              iconName="close"
              variant="transparent"
              size="small"
              className="absolute !inset-0 !h-auto !p-0 opacity-0 transition-opacity group-hover:opacity-100"
              handleClick={() => handleCancelDownload(downloadId)}
            />
          </div>
        );
    }
  };

  if (downloads.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 min-w-96 max-w-150 rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="flex items-center justify-between border-b border-gray-200 p-4">
        <h3 className="text-base font-medium text-gray-800">
          Download will start once the files are ready
        </h3>
        <IconButton
          iconName="close"
          variant="transparent"
          size="medium"
          className="hidden"
          handleClick={() => dispatch(removeAllDownloads())}
        />
      </div>

      <div className="max-h-96 overflow-y-auto">
        {downloads.map((download) => (
          <div
            key={download.id}
            className="flex items-center border-b border-gray-100 p-4 last:border-b-0"
          >
            <div className="flex min-w-0 flex-1 items-center">
              <Icon name="file" className="mr-3" />
              <span className="truncate text-sm text-gray-700">
                {download.fileName}.{download.fileType}
              </span>
            </div>

            <div className="ml-4 flex items-center">
              <span
                className={`text-xs ${getDownloadStatusColor(download.status)}`}
              >
                {getDownloadStatusIcon(download.status, download.id)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DownloadProgressPanel;
